import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { t } from '../i18n';
import { generateQuestion } from '../gameEngine';
import { generateMCQuestion } from '../questionBanks';
import TeamPanel from '../components/game/TeamPanel';
import TugOfWarStage from '../components/game/TugOfWarStage';
import SoundToggle from '../components/game/SoundToggle';
import { motion, AnimatePresence } from 'framer-motion';
import {
  playCorrectSound, playWrongSound, playStreakSound,
  playSuperStreakSound, playCountdownSound, playGoSound,
  playWinSound, playTimerUrgentSound, playKeypadSound
} from '../soundEngine';

const WIN_THRESHOLD = 5;
const STREAK_BONUS_AT = 3;
const SUPER_STREAK_AT = 5;

export default function GamePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const settings = location.state;
  const lastUrgentRef = useRef(0);

  const isMCQ = settings?.subjectType === 'mcq';
  const subjectId = settings?.subjectId || 'matematik';
  const gameLang = settings?.language || language;
  const aiQuestions = settings?.aiQuestions || [];
  const aiQueueRef = useRef([...aiQuestions]);

  const [countdown, setCountdown] = useState(3);
  const [gameStarted, setGameStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(settings?.duration || 60);
  const [gameOver, setGameOver] = useState(false);
  const [streakEffects, setStreakEffects] = useState([]);
  const [explanation, setExplanation] = useState(null);

  const [team1, setTeam1] = useState({
    question: null, input: '', score: 0, correct: 0, wrong: 0,
    feedback: null, streak: 0, bestStreak: 0, comboText: null, mcqSelected: null,
  });
  const [team2, setTeam2] = useState({
    question: null, input: '', score: 0, correct: 0, wrong: 0,
    feedback: null, streak: 0, bestStreak: 0, comboText: null, mcqSelected: null,
  });

  const genQuestion = useCallback(() => {
    // Try AI questions first
    if (aiQueueRef.current.length > 0) {
      const q = aiQueueRef.current.shift();
      return {
        q_en: q.q_en, q_bm: q.q_bm,
        options_en: q.options_en || q.options, options_bm: q.options_bm || q.options,
        answer: q.answer,
        explanation_en: q.explanation_en || '', explanation_bm: q.explanation_bm || '',
        isAI: true,
      };
    }
    // Fallback to static bank (with progressive difficulty for math)
    if (isMCQ) {
      return generateMCQuestion(subjectId, settings?.difficulty || 'easy', gameLang);
    }
    const totalCorrect = (team1?.correct || 0) + (team2?.correct || 0);
    return generateQuestion(settings?.difficulty || 'easy', settings?.operations || ['+'], totalCorrect);
  }, [isMCQ, subjectId, settings, gameLang, team1?.correct, team2?.correct]);

  useEffect(() => {
    if (!settings) { navigate('/setup'); return; }
    setTeam1(prev => ({ ...prev, question: genQuestion() }));
    setTeam2(prev => ({ ...prev, question: genQuestion() }));
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      playCountdownSound();
      const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && !gameStarted) {
      playGoSound();
      setGameStarted(true);
    }
  }, [countdown, gameStarted]);

  useEffect(() => {
    if (gameStarted && !gameOver && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 11 && prev > 1) {
            const now = Date.now();
            if (now - lastUrgentRef.current > 900) { playTimerUrgentSound(); lastUrgentRef.current = now; }
          }
          if (prev <= 1) { setGameOver(true); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [gameStarted, gameOver, timeLeft]);

  const ropePosition = team1.score - team2.score;

  useEffect(() => {
    if (Math.abs(ropePosition) >= WIN_THRESHOLD && gameStarted) setGameOver(true);
  }, [ropePosition, gameStarted]);

  useEffect(() => {
    if (gameOver && settings) {
      playWinSound();
      const timer = setTimeout(() => {
        let winner = 'draw';
        if (team1.score > team2.score) winner = 'team1';
        else if (team2.score > team1.score) winner = 'team2';
        navigate('/results', {
          state: {
            team1Name: settings.team1Name, team2Name: settings.team2Name,
            team1Score: team1.score, team2Score: team2.score,
            team1Correct: team1.correct, team1Wrong: team1.wrong,
            team2Correct: team2.correct, team2Wrong: team2.wrong,
            team1BestStreak: team1.bestStreak, team2BestStreak: team2.bestStreak,
            winner, difficulty: settings.difficulty,
            operations: settings.operations || [], duration: settings.duration,
            subjectId,
            subjectType: isMCQ ? 'mcq' : 'math',
            schoolName: settings.schoolName || '',
            language: settings.language || language,
          }
        });
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [gameOver]);

  const addStreakEffect = useCallback((team, text, color) => {
    const id = Date.now() + Math.random();
    setStreakEffects(prev => [...prev, { id, team, text, color }]);
    setTimeout(() => setStreakEffects(prev => prev.filter(e => e.id !== id)), 1500);
  }, []);

  const processAnswer = useCallback((team, isCorrect) => {
    const setter = team === 'team1' ? setTeam1 : setTeam2;
    const state = team === 'team1' ? team1 : team2;

    if (isCorrect) {
      const newStreak = state.streak + 1;
      let bonusPoints = 0;
      let comboText = null;

      if (newStreak >= SUPER_STREAK_AT) {
        bonusPoints = 2; comboText = `SUPER STREAK! x${newStreak}`;
        playSuperStreakSound(); addStreakEffect(team, `SUPER x${newStreak}! +3`, '#ff6b00');
      } else if (newStreak >= STREAK_BONUS_AT) {
        bonusPoints = 1; comboText = `STREAK! x${newStreak}`;
        playStreakSound(); addStreakEffect(team, `STREAK x${newStreak}! +2`, '#22c55e');
      } else { playCorrectSound(); }

      setter(prev => ({
        ...prev, score: prev.score + 1 + bonusPoints, correct: prev.correct + 1,
        input: '', feedback: 'correct', streak: newStreak,
        bestStreak: Math.max(prev.bestStreak, newStreak), comboText,
        question: genQuestion(), mcqSelected: null,
      }));
    } else {
      playWrongSound();
      // Show explanation if available
      const q = state.question;
      if (q && (q.explanation_en || q.explanation_bm)) {
        const expText = gameLang === 'en' ? (q.explanation_en || q.explanation_bm) : (q.explanation_bm || q.explanation_en);
        setExplanation(expText);
        setTimeout(() => setExplanation(null), 2500);
      }
      setter(prev => ({ ...prev, wrong: prev.wrong + 1, input: '', feedback: 'wrong', streak: 0, comboText: null, question: genQuestion() }));
    }
    setTimeout(() => { setter(prev => ({ ...prev, feedback: null, comboText: null, mcqSelected: null })); }, 600);
  }, [team1, team2, genQuestion, addStreakEffect]);

  // MCQ handler
  const handleMCQSelect = useCallback((team, selectedIndex) => {
    if (gameOver || !gameStarted) return;
    const state = team === 'team1' ? team1 : team2;
    const setter = team === 'team1' ? setTeam1 : setTeam2;
    setter(prev => ({ ...prev, mcqSelected: selectedIndex }));
    const isCorrect = selectedIndex === state.question.answer;
    processAnswer(team, isCorrect);
  }, [gameOver, gameStarted, team1, team2, processAnswer]);

  // Keypad handlers (math mode)
  const handleDigit = useCallback((team, digit) => {
    if (gameOver || !gameStarted) return;
    playKeypadSound();
    const setter = team === 'team1' ? setTeam1 : setTeam2;
    setter(prev => ({ ...prev, input: prev.input.length < 4 ? prev.input + digit : prev.input }));
  }, [gameOver, gameStarted]);

  const handleClear = useCallback((team) => {
    if (gameOver || !gameStarted) return;
    const setter = team === 'team1' ? setTeam1 : setTeam2;
    setter(prev => ({ ...prev, input: '' }));
  }, [gameOver, gameStarted]);

  const handleSubmit = useCallback((team) => {
    if (gameOver || !gameStarted) return;
    const state = team === 'team1' ? team1 : team2;
    if (state.input === '') return;
    const isCorrect = state.input === state.question.answer.toString();
    processAnswer(team, isCorrect);
  }, [gameOver, gameStarted, team1, team2, processAnswer]);

  if (!settings) return null;

  return (
    <div className="game-page">
      <SoundToggle className="game-sound-toggle" />
      <AnimatePresence>
        {countdown > 0 && (
          <motion.div className="countdown-overlay" initial={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div key={countdown} className="countdown-number"
              initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 2, opacity: 0 }} transition={{ duration: 0.6 }}>
              {countdown}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {gameOver && (
          <motion.div className="gameover-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
            <motion.div className="gameover-text" initial={{ scale: 0.5 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200 }}>
              {timeLeft === 0 ? t('timesUp', language) : t('gameOver', language)}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Explanation popup */}
      <AnimatePresence>
        {explanation && (
          <motion.div
            data-testid="explanation-popup"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            style={{
              position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
              background: '#1e293b', border: '1px solid #38bdf8', borderRadius: 16,
              padding: '12px 20px', maxWidth: 400, zIndex: 500, textAlign: 'center',
              boxShadow: '0 0 20px rgba(56,189,248,0.2)',
            }}>
            <div style={{ fontSize: 11, color: '#38bdf8', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              {gameLang === 'en' ? 'Explanation' : 'Penjelasan'}
            </div>
            <div style={{ fontSize: 14, color: '#f8fafc', fontWeight: 600, lineHeight: 1.5 }}>{explanation}</div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {streakEffects.map(effect => (
          <motion.div key={effect.id} className={`streak-effect streak-effect-${effect.team}`}
            style={{ color: effect.color }}
            initial={{ opacity: 0, y: 20, scale: 0.5 }} animate={{ opacity: 1, y: -40, scale: 1.2 }}
            exit={{ opacity: 0, y: -80 }} transition={{ duration: 1.2 }}>
            {effect.text}
          </motion.div>
        ))}
      </AnimatePresence>

      <div className="game-layout">
        <TeamPanel team="team1" teamName={settings.team1Name}
          question={team1.question} input={team1.input} feedback={team1.feedback}
          comboText={team1.comboText} streak={team1.streak}
          isMCQ={isMCQ} mcqSelectedIndex={team1.mcqSelected}
          onDigit={(d) => handleDigit('team1', d)} onClear={() => handleClear('team1')}
          onSubmit={() => handleSubmit('team1')}
          onMCQSelect={(idx) => handleMCQSelect('team1', idx)}
          disabled={!gameStarted || gameOver} language={language} />

        <TugOfWarStage ropePosition={ropePosition}
          team1Score={team1.score} team2Score={team2.score}
          team1Name={settings.team1Name} team2Name={settings.team2Name}
          timeLeft={timeLeft} maxPosition={WIN_THRESHOLD}
          gameOver={gameOver} language={language} />

        <TeamPanel team="team2" teamName={settings.team2Name}
          question={team2.question} input={team2.input} feedback={team2.feedback}
          comboText={team2.comboText} streak={team2.streak}
          isMCQ={isMCQ} mcqSelectedIndex={team2.mcqSelected}
          onDigit={(d) => handleDigit('team2', d)} onClear={() => handleClear('team2')}
          onSubmit={() => handleSubmit('team2')}
          onMCQSelect={(idx) => handleMCQSelect('team2', idx)}
          disabled={!gameStarted || gameOver} language={language} />
      </div>
    </div>
  );
}
